import React, { useState } from 'react';
import { FolderOpen, Youtube, ImageIcon, Play, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import MediaPicker from './MediaPicker';

// ---- helpers ----
export function extractYouTubeId(input) {
  if (!input) return '';
  const s = String(input).trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(s)) return s;
  const m =
    s.match(/[?&]v=([a-zA-Z0-9_-]{11})/) ||
    s.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/) ||
    s.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/) ||
    s.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : s;
}

// ---- reusable fields ----
export const TextField = ({ label, value, onChange, placeholder, className = '' }) => (
  <div className={className}>
    <Label className="text-[11px] uppercase tracking-widest text-slate-400">{label}</Label>
    <Input
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1.5 bg-black/30 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-brand-green/60"
    />
  </div>
);

export const TextAreaField = ({ label, value, onChange, placeholder, rows = 3, className = '' }) => (
  <div className={className}>
    <Label className="text-[11px] uppercase tracking-widest text-slate-400">{label}</Label>
    <Textarea
      value={value ?? ''}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1.5 bg-black/30 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-brand-green/60 resize-none"
    />
  </div>
);

export const ImageField = ({ label, value, onChange, className = '' }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={className}>
      <Label className="text-[11px] uppercase tracking-widest text-slate-400">{label}</Label>
      <div className="mt-1.5 flex flex-col sm:flex-row gap-2 items-start">
        <div className="h-16 w-24 rounded-lg overflow-hidden border border-white/10 bg-black/30 flex-shrink-0 flex items-center justify-center">
          {value ? (
            <img src={value} alt="preview" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-4 w-4 text-slate-600" />
          )}
        </div>
        <div className="flex-1 w-full flex gap-2">
          <Input
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://… or paste from library"
            className="bg-black/30 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-brand-green/60"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(true)}
            className="border-white/15 bg-white/5 hover:bg-white/10 text-white btn-transition whitespace-nowrap"
          >
            <FolderOpen className="h-4 w-4 mr-1.5" /> Library
          </Button>
        </div>
      </div>
      <MediaPicker open={open} kind="image" onPick={(u) => { onChange(u); setOpen(false); }} onClose={() => setOpen(false)} />
    </div>
  );
};

export const VideoIdField = ({ label, value, onChange, className = '' }) => {
  const id = extractYouTubeId(value);
  return (
    <div className={className}>
      <Label className="text-[11px] uppercase tracking-widest text-slate-400 flex items-center gap-1"><Youtube className="h-3 w-3 text-brand-pink-light" /> {label}</Label>
      <div className="mt-1.5 flex gap-2">
        <Input
          value={value ?? ''}
          onChange={(e) => onChange(extractYouTubeId(e.target.value))}
          placeholder="Paste YouTube URL or 11-char ID"
          className="bg-black/30 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-brand-green/60"
        />
        {id && id.length === 11 && (
          <a
            href={`https://www.youtube.com/watch?v=${id}`}
            target="_blank"
            rel="noreferrer"
            className="h-10 px-3 rounded-md border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center gap-1.5 text-xs btn-transition"
          >
            <Play className="h-3.5 w-3.5" /> Preview
          </a>
        )}
      </div>
      {value && (id.length !== 11) && (
        <p className="mt-1 text-[11px] text-brand-pink-light">Invalid YouTube ID (need 11 characters)</p>
      )}
    </div>
  );
};

export const Section = ({ title, subtitle, children, defaultOpen = false, id }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section id={id} className="rounded-2xl border border-white/8 bg-white/[0.02] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-white/[0.03] btn-transition"
      >
        <div>
          <div className="text-white font-medium">{title}</div>
          {subtitle && <div className="text-xs text-slate-400 mt-0.5">{subtitle}</div>}
        </div>
        <span className={`text-slate-400 text-xs btn-transition ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open && <div className="px-6 pb-6 pt-2 space-y-4 border-t border-white/5">{children}</div>}
    </section>
  );
};

export const ListEditor = ({ items = [], onChange, addLabel = 'Add item', factory, renderItem }) => {
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    onChange(arr);
  };
  const update = (idx, next) => onChange(items.map((it, i) => (i === idx ? next : it)));
  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div key={idx} className="rounded-xl border border-white/8 bg-black/20 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest text-slate-400">Item #{idx + 1}</div>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => move(idx, -1)} className="text-xs text-slate-400 hover:text-white btn-transition px-2 py-0.5 rounded" disabled={idx === 0}>↑</button>
              <button type="button" onClick={() => move(idx, 1)} className="text-xs text-slate-400 hover:text-white btn-transition px-2 py-0.5 rounded" disabled={idx === items.length - 1}>↓</button>
              <button type="button" onClick={() => remove(idx)} className="text-xs text-brand-pink-light hover:text-white btn-transition px-2 py-0.5 rounded flex items-center gap-1"><X className="h-3 w-3" /> Remove</button>
            </div>
          </div>
          {renderItem(item, (next) => update(idx, next))}
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={() => onChange([...items, factory()])}
        className="border-dashed border-white/15 bg-transparent hover:bg-white/5 text-white btn-transition w-full"
      >
        + {addLabel}
      </Button>
    </div>
  );
};
